import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

const LANGUAGES = [
  { code: 'en', label: 'EN', full: 'English', flag: '\u{1F1EC}\u{1F1E7}' },
  { code: 'th', label: '\u0E44\u0E17\u0E22', full: '\u0E20\u0E32\u0E29\u0E32\u0E44\u0E17\u0E22', flag: '\u{1F1F9}\u{1F1ED}' },
  { code: 'vi', label: 'VI', full: 'Ti\u1EBFng Vi\u1EC7t', flag: '\u{1F1FB}\u{1F1F3}' },
  { code: 'fr', label: 'FR', full: 'Fran\u00E7ais', flag: '\u{1F1EB}\u{1F1F7}' },
  { code: 'ru', label: 'RU', full: '\u0420\u0443\u0441\u0441\u043A\u0438\u0439', flag: '\u{1F1F7}\u{1F1FA}' },
  { code: 'de', label: 'DE', full: 'Deutsch', flag: '\u{1F1E9}\u{1F1EA}' },
  { code: 'ko', label: 'KO', full: '한국어', flag: '\u{1F1F0}\u{1F1F7}' },
  { code: 'zh', label: 'ZH', full: '中文', flag: '\u{1F1E8}\u{1F1F3}' },
  { code: 'ja', label: 'JA', full: '日本語', flag: '\u{1F1EF}\u{1F1F5}' },
  { code: 'es', label: 'ES', full: 'Español', flag: '\u{1F1EA}\u{1F1F8}' },
  { code: 'it', label: 'IT', full: 'Italiano', flag: '\u{1F1EE}\u{1F1F9}' },
  { code: 'ms', label: 'MS', full: 'Bahasa Melayu', flag: '\u{1F1F2}\u{1F1FE}' },
  { code: 'he', label: 'HE', full: 'עברית', flag: '\u{1F1EE}\u{1F1F1}' },
  { code: 'pt', label: 'PT', full: 'Português', flag: '\u{1F1E7}\u{1F1F7}' },
];

export default function LanguageSwitcher({ style }) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);

  const current = LANGUAGES.find(l => l.code === i18n.language) || LANGUAGES[0];

  const select = (code) => {
    i18n.changeLanguage(code);
    // Update <html lang=""> for screen readers and CSS :lang() selector
    document.documentElement.lang = code;
    // Apply Thai font class to body
    document.body.className = document.body.className
      .replace(/\blang-\w+\b/g, '')
      .trim();
    if (code === 'th') document.body.classList.add('lang-th');
    setOpen(false);
    // Save to localStorage (i18next-browser-languagedetector does this, but be explicit)
    localStorage.setItem('hl_locale', code);
  };

  return (
    <div style={{ position: 'relative', display: 'inline-block', ...style }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          background: 'rgba(24, 74, 153, 0.9)',
          border: '1px solid rgba(255,255,255,0.3)',
          borderRadius: 6,
          padding: '4px 8px',
          cursor: 'pointer',
          color: 'white',
          fontSize: 13,
          fontWeight: 500,
        }}
        aria-label={t('language.selectLanguageAria')}
      >
        <span>{current.flag}</span>
        <span>{current.label}</span>
        <span style={{ fontSize: 10, opacity: 0.7 }}>{'\u25BE'}</span>
      </button>

      {open && (
        <>
          {/* Backdrop */}
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 999 }}
            onClick={() => setOpen(false)}
          />
          {/* Dropdown */}
          <div style={{
            position: 'absolute',
            right: 0,
            top: 'calc(100% + 4px)',
            background: '#1a1a2e',
            border: '1px solid rgba(255,255,255,0.15)',
            borderRadius: 8,
            overflow: 'hidden',
            minWidth: 160,
            zIndex: 1000,
            boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
          }}>
            {LANGUAGES.map(lang => (
              <button
                key={lang.code}
                onClick={() => select(lang.code)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  width: '100%',
                  padding: '10px 14px',
                  background: lang.code === i18n.language ? 'rgba(99,102,241,0.2)' : 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: lang.code === i18n.language ? '#818cf8' : '#e2e8f0',
                  fontSize: 14,
                  textAlign: 'left',
                  transition: 'background 0.15s',
                }}
                onMouseEnter={e => { if (lang.code !== i18n.language) e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
                onMouseLeave={e => { if (lang.code !== i18n.language) e.currentTarget.style.background = 'none'; }}
              >
                <span style={{ fontSize: 18 }}>{lang.flag}</span>
                <div>
                  <div style={{ fontWeight: 500 }}>{lang.full}</div>
                  <div style={{ fontSize: 11, opacity: 0.5 }}>{lang.code.toUpperCase()}</div>
                </div>
                {lang.code === i18n.language && (
                  <span style={{ marginLeft: 'auto', color: '#818cf8', fontSize: 12 }}>{'\u2713'}</span>
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
